import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, KeyRound, Download, Trash2, AlertTriangle, Copy, Check, Mail, PhoneCall,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { date, dateTime, relative } from '@/lib/format';
import { saveFile } from '@/lib/download';
import {
  Badge, Button, Card, CardHeader, ConfirmDialog, Field, Input, LoadingBlock,
  Modal, PageHeader, Select, Table, TableWrap, Td, Th, useToast, statusTone,
} from '@/components/ui';
import type { BadgeTone } from '@/components/ui';
import { CallPanel } from '@/components/CallPanel';
import type {
  AdminEmailRow, AdminOrgDetail, AdminSenderOptions, StaffRole, User,
} from '@shared/types';

const STATUSES = ['trialing', 'active', 'lapsed', 'cancelled'];
const PLANS = ['starter', 'standard', 'custom'];
const ROLES: StaffRole[] = ['owner', 'manager', 'staff', 'viewer'];

/**
 * The most advanced thing we know about one message.
 *
 * Later facts win over earlier ones: a mail that bounced was still "sent" a
 * moment before, and showing the handover status for a message we know was
 * rejected is how somebody carries on waiting for a reply that cannot come.
 * Opened is deliberately last and only shown when open tracking is on, where
 * it is a guess about a person rather than a fact about delivery.
 */
function deliveryState(row: AdminEmailRow): { label: string; tone: BadgeTone } {
  if (row.bounced_at) return { label: 'bounced', tone: 'danger' };
  if (row.opened_at) return { label: 'opened', tone: 'ok' };
  if (row.delivered_at) return { label: 'delivered', tone: 'ok' };
  return { label: row.status, tone: statusTone(row.status) };
}

export default function OrganisationDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, reload } = useApi<AdminOrgDetail>(id ? `/admin/organisations/${id}` : null);

  const org = data?.organisation;

  const [form, setForm] = useState({
    name: '', contact_email: '', contact_phone: '', plan: 'starter',
    property_limit: 5, subscription_status: 'trialing', trial_ends_at: '',
    sms_from_number: '', email_from_address: '', email_from_name: '',
  });
  const [busy, setBusy] = useState(false);
  const [calling, setCalling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [resetLink, setResetLink] = useState<{ user: string; link: string } | null>(null);
  const [exportSet, setExportSet] = useState('payments');
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);

  /*
   * The numbers on the messaging account, and who already has each. Assigning
   * by choosing from this list rather than typing one from memory is the
   * difference between a mistake caught now and a mistake caught days later as
   * a failed send.
   */
  const { data: senders } = useApi<AdminSenderOptions>('/admin/twilio/numbers');

  useEffect(() => {
    if (!org) return;
    setForm({
      name: org.name,
      contact_email: org.contact_email ?? '',
      contact_phone: org.contact_phone ?? '',
      plan: org.plan,
      property_limit: org.property_limit,
      subscription_status: org.subscription_status,
      trial_ends_at: org.trial_ends_at ?? '',
      sms_from_number: org.sms_from_number ?? '',
      email_from_address: org.email_from_address ?? '',
      email_from_name: org.email_from_name ?? '',
    });
  }, [org]);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function save() {
    setBusy(true);
    try {
      const saved = await api.patch<{ reply_note?: string }>(`/admin/organisations/${id}`, {
        ...form,
        property_limit: Number(form.property_limit),
        trial_ends_at: form.trial_ends_at || null,
      });
      // Said back rather than left to be discovered: the whole point of a
      // sending address is that a reply reaches the landlord, and there are
      // two ways for that to be quietly untrue.
      toast.success(saved?.reply_note ?? 'Saved');
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  async function updateUser(user: User, patch: Record<string, unknown>) {
    try {
      await api.patch(`/admin/users/${user.id}`, patch);
      toast.success(`${user.full_name} updated`);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update that account');
    }
  }

  async function issueResetLink(user: User) {
    try {
      const res = await api.post<{ link: string; email_status: string }>(
        `/admin/users/${user.id}/reset-link`, {},
      );
      setResetLink({ user: user.full_name, link: res.link });
      toast.success(res.email_status === 'sent'
        ? `Reset link emailed to ${user.email}`
        : `Link created — email was ${res.email_status}, so copy it below`);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create a reset link');
    }
  }

  /**
   * Everything this customer has, as one JSON file.
   *
   * The right shape for handing an account over or taking a copy before doing
   * something irreversible, and the wrong shape for "send me their payments as
   * a spreadsheet" — which is what support actually gets asked for, and what
   * the picker below does.
   */
  async function exportOrg() {
    try {
      const res = await api.get(`/admin/organisations/${id}/export`);
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(org?.name ?? 'organisation').replace(/[^\w-]+/g, '-').toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not export');
    }
  }

  async function exportPart() {
    setExporting(true);
    try {
      const file = await api.post(`/admin/organisations/${id}/export`, {
        dataset: exportSet, format: exportFormat,
      });
      saveFile(file);
      toast.success(`Exported ${file.rows} ${file.rows === 1 ? 'record' : 'records'}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not build that export');
    } finally {
      setExporting(false);
    }
  }

  async function destroy() {
    setBusy(true);
    try {
      await api.post(`/admin/organisations/${id}/delete`, { confirm_name: confirmName });
      toast.success('Organisation deleted');
      navigate('/admin');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete');
      setBusy(false);
    }
  }

  if (loading) return <LoadingBlock rows={8} />;
  if (!org) {
    return (
      <div className="text-muted">
        That organisation was not found. <Link to="/admin" className="link">Back to the list</Link>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumb={(
          <Link to="/admin" className="flex items-center gap-1.5 text-sm text-muted hover:text-ink w-fit">
            <ArrowLeft className="h-4 w-4" /> Organisations
          </Link>
        )}
        title={org.name}
        subtitle={`Signed up ${date(org.created_at)} · ${org.counts.properties} properties · ${org.counts.tenants} tenants · ${org.counts.active_leases} live leases`}
        action={(
          <>
            {/*
              Shown only when there is a number to ring, and it uses the saved
              one rather than whatever is currently typed in the form above —
              calling a number somebody is halfway through editing is how you
              ring the wrong person.
            */}
            {org.contact_phone && (
              <Button
                variant="secondary"
                icon={<PhoneCall className="h-4 w-4" />}
                onClick={() => setCalling(true)}
              >
                Call
              </Button>
            )}
            <Button variant="secondary" icon={<Download className="h-4 w-4" />} onClick={() => void exportOrg()}>
              Export
            </Button>
            <Button variant="primary" onClick={() => void save()} loading={busy}>
              Save changes
            </Button>
          </>
        )}
      />

      {calling && org.contact_phone && (
        <CallPanel
          target={{ orgId: org.id, name: org.name, phone: org.contact_phone }}
          onClose={() => setCalling(false)}
        />
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader title="Account" subtitle="Who this customer is" />
            <div className="p-5 sm:p-6 pt-4 space-y-4">
              <Field label="Name">
                <Input value={form.name} onChange={set('name')} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Contact email">
                  <Input type="email" value={form.contact_email} onChange={set('contact_email')} />
                </Field>
                <Field label="Contact phone">
                  <Input value={form.contact_phone} onChange={set('contact_phone')}
                    placeholder="+447700900123" />
                </Field>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Subscription"
              subtitle="Nothing here charges anybody — it records what was agreed"
            />
            <div className="p-5 sm:p-6 pt-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Status">
                  <Select value={form.subscription_status} onChange={set('subscription_status')}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </Field>
                <Field
                  label="Trial ends"
                  hint="A trialing account past this date reads as lapsed"
                >
                  <Input type="date" value={form.trial_ends_at} onChange={set('trial_ends_at')} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Plan">
                  <Select value={form.plan} onChange={set('plan')}>
                    {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </Select>
                </Field>
                <Field
                  label="Property limit"
                  hint="Held on the account, so a negotiated deal is just a number"
                >
                  <Input type="number" min={0} value={form.property_limit}
                    onChange={set('property_limit')} />
                </Field>
              </div>

              {/*
                A lapsed account is read-only, not locked. Worth saying on the
                screen where somebody is about to set that status, because the
                natural fear — "am I about to take away their records?" — is
                the thing that makes people avoid using this correctly.
              */}
              {form.subscription_status === 'lapsed' && (
                <p className="text-sm text-muted leading-relaxed bg-raised border
                              border-line rounded-xl px-3 py-2.5">
                  Lapsed means read-only. They keep every record, can still export
                  everything and can still sign in — they just cannot record new work
                  until this is set back to active.
                </p>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Staff accounts"
              subtitle="The people who sign into this organisation"
            />
            {data.users.length === 0 ? (
              <p className="px-5 sm:px-6 pb-6 text-sm text-muted">Nobody yet.</p>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Name</Th>
                      <Th>Role</Th>
                      <Th>Last sign in</Th>
                      <Th align="right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((user) => (
                      <tr key={user.id}>
                        <Td>
                          <div className="font-medium text-ink">{user.full_name}</div>
                          <div className="text-xs text-faint">{user.email}</div>
                        </Td>
                        <Td>
                          <Select
                            value={user.role}
                            onChange={(e) => void updateUser(user, { role: e.target.value })}
                            className="h-8 text-xs w-28"
                          >
                            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                          </Select>
                        </Td>
                        <Td>
                          {user.is_active
                            ? <span className="text-muted">{relative(user.last_login_at)}</span>
                            : <Badge tone="danger">deactivated</Badge>}
                        </Td>
                        <Td align="right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              className="h-8 px-2 text-xs"
                              icon={<KeyRound className="h-3.5 w-3.5" />}
                              onClick={() => void issueResetLink(user)}
                              disabled={!user.is_active}
                            >
                              Reset link
                            </Button>
                            <Button
                              variant="ghost"
                              className="h-8 px-2 text-xs"
                              onClick={() => void updateUser(user, { is_active: !user.is_active })}
                            >
                              {user.is_active ? 'Deactivate' : 'Reactivate'}
                            </Button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
            {/*
              Says what the button does not do, because "reset link" could
              reasonably be read as "set their password", and the difference
              matters: an admin who could type a customer's password could sign
              in as them, and every action afterwards would be recorded as
              theirs.
            */}
            <p className="px-5 sm:px-6 pb-5 pt-1 text-sm text-muted leading-relaxed">
              A reset link lets them choose their own password. It does not set one, and it
              does not let you sign in as them.
            </p>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="How their messages are addressed" />
            <div className="p-5 sm:p-6 pt-4 space-y-4">
              {/*
                Anything they have asked for, first. It is the reason somebody
                opened this card, and burying it under the fields means the
                request is answered by accident or not at all.
              */}
              {data.open_requests.length > 0 && (
                <div className="rounded-xl bg-warn/10 border border-warn/25 p-3 space-y-1">
                  <p className="text-sm font-medium">They have asked for:</p>
                  <ul className="text-sm text-muted space-y-0.5">
                    {data.open_requests.map((r) => (
                      <li key={r.kind}>
                        {r.kind === 'assign_sms_number'
                          ? 'a phone number of their own'
                          : 'a sending address of their own'}
                        {' · '}
                        {date(r.created_at)}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-faint">
                    Setting one below closes the job on its own.
                  </p>
                </div>
              )}

              <Field
                label="Texts come from"
                hint={
                  senders?.configured === false
                    ? 'Texting is not switched on for this deployment.'
                    : senders && !senders.reachable
                      ? 'Could not reach Twilio to list the numbers — try again in a moment.'
                      : 'A number on our own messaging account. Blank uses the shared sender.'
                }
              >
                <Select value={form.sms_from_number} onChange={set('sms_from_number')}>
                  <option value="">Shared sender</option>
                  {(senders?.numbers ?? []).map((n) => (
                    <option
                      key={n.number}
                      value={n.number}
                      // Taken by somebody else, or cannot send at all. Two
                      // organisations on one number means a tenant replying to
                      // one reaches the other.
                      disabled={(!!n.assigned_to && n.assigned_to !== org.name) || !n.sms}
                    >
                      {n.number}
                      {n.name ? ` — ${n.name}` : ''}
                      {!n.sms ? ' (no SMS)' : n.assigned_to ? ` (${n.assigned_to})` : ''}
                    </option>
                  ))}
                  {/*
                    A number already on the record that Twilio no longer lists
                    must still be selectable, or saving any other field on this
                    form would silently clear it.
                  */}
                  {form.sms_from_number
                    && !(senders?.numbers ?? []).some((n) => n.number === form.sms_from_number) && (
                    <option value={form.sms_from_number}>
                      {form.sms_from_number} (not on the account)
                    </option>
                  )}
                </Select>
              </Field>

              {/*
                A list is convenient until it is wrong. Twilio may be
                unreachable, a number may have been bought a minute ago, or
                this deployment may not use Twilio for texts at all — and a
                picker with no way to type is a dead end in all three cases.
                The value is validated on save either way.
              */}
              <Field
                label="Or type a number"
                hint="For one just bought, or when the list above cannot be loaded."
              >
                <Input
                  value={form.sms_from_number}
                  onChange={set('sms_from_number')}
                  placeholder="+447700900123"
                />
              </Field>

              <Field
                label="Email comes from"
                hint={
                  senders?.allowed_email_domains?.length
                    ? `Must be on ${senders.allowed_email_domains.join(' or ')}`
                    : 'Must be on a domain this deployment can send as'
                }
              >
                <div className="flex gap-2">
                  <Input
                    value={form.email_from_address}
                    onChange={set('email_from_address')}
                    placeholder={`${suggestLocalPart(org.name)}@${senders?.allowed_email_domains?.[0] ?? 'provia.uk'}`}
                  />
                  {!form.email_from_address && senders?.allowed_email_domains?.[0] && (
                    <Button
                      onClick={() => setForm((f) => ({
                        ...f,
                        email_from_address: `${suggestLocalPart(org.name)}@${senders.allowed_email_domains[0]}`,
                        email_from_name: f.email_from_name || org.name,
                      }))}
                    >
                      Suggest
                    </Button>
                  )}
                </div>
              </Field>
              <Field label="Shown as">
                <Input value={form.email_from_name} onChange={set('email_from_name')}
                  placeholder={org.name} />
              </Field>

              <p className="text-xs text-faint leading-relaxed">
                {senders?.email_reply_to_header === false
                  ? `Our mail provider carries no reply-to header, so ${org.contact_email ?? 'their contact address'} is written into the message body instead.`
                  : `Replies go to ${org.contact_email ?? 'their contact address — which is not set'}. The sending address needs no mailbox.`}
              </p>
            </div>
          </Card>

          <Card>
            <CardHeader title="Recent email" subtitle="What we have sent them" />
            <div className="p-5 sm:p-6 pt-4">
              {data.recent_email.length === 0 ? (
                <p className="text-sm text-muted">Nothing yet.</p>
              ) : (
                <div className="space-y-3">
                  {data.recent_email.slice(0, 8).map((row) => {
                    const delivery = deliveryState(row);
                    return (
                      <div key={row.id} className="flex items-start gap-2.5 text-sm">
                        <Mail className="h-4 w-4 shrink-0 mt-0.5 text-faint" />
                        <div className="min-w-0 flex-1">
                          <p className="text-ink truncate">{row.subject}</p>
                          <p className="text-xs text-faint">
                            {row.to_email} · {dateTime(row.created_at)}
                          </p>
                          {row.bounce_reason && (
                            <p className="text-xs text-danger mt-0.5">{row.bounce_reason}</p>
                          )}
                          {!row.bounce_reason && row.error_message && (
                            <p className="text-xs text-danger mt-0.5">{row.error_message}</p>
                          )}
                        </div>
                        <Badge tone={delivery.tone}>{delivery.label}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          {/*
            The export support is actually asked for. "Send us their payments
            as a spreadsheet" is a request that used to mean an operator
            opening the spreadsheet by hand; the whole-account JSON at the top
            of this page answers a different question.
          */}
          <Card>
            <CardHeader title="Export part of this account" subtitle="The same file they would get themselves" />
            <div className="p-5 sm:p-6 pt-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Select value={exportSet} onChange={(e) => setExportSet(e.target.value)}>
                  <option value="tenants">Tenants</option>
                  <option value="properties">Properties</option>
                  <option value="rooms">Rooms and units</option>
                  <option value="leases">Tenancies</option>
                  <option value="charges">Charges</option>
                  <option value="payments">Payments</option>
                  <option value="bills">Bills</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="messages">Messages</option>
                  <option value="documents">Document list</option>
                  <option value="schedules">Standing orders</option>
                  <option value="bank">Bank transactions</option>
                </Select>
                <Select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
                  <option value="csv">Spreadsheet (CSV)</option>
                  <option value="pdf">Document (PDF)</option>
                  <option value="txt">Plain text</option>
                  <option value="json">Data file (JSON)</option>
                </Select>
              </div>
              <Button
                className="w-full"
                loading={exporting}
                icon={<Download className="h-4 w-4" />}
                onClick={() => void exportPart()}
              >
                Download
              </Button>
            </div>
          </Card>

          <Card className="border-danger/30">
            <CardHeader
              title={<span className="text-danger">Delete this organisation</span>}
              subtitle="Every record and every stored document"
            />
            <div className="p-5 sm:p-6 pt-4 space-y-3">
              <p className="text-sm text-muted leading-relaxed">
                For an erasure request from a customer who cannot do it themselves. It cannot
                be undone — export first.
              </p>
              <Button
                variant="danger"
                className="w-full"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => { setConfirmName(''); setDeleting(true); }}
              >
                Delete {org.name}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <Modal
        open={Boolean(resetLink)}
        onClose={() => setResetLink(null)}
        title="Password reset link"
        subtitle={resetLink ? `For ${resetLink.user}. It expires in an hour and works once.` : ''}
      >
        {resetLink && <CopyableLink link={resetLink.link} />}
      </Modal>

      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => void destroy()}
        title={`Delete ${org.name}?`}
        confirmLabel="Delete permanently"
        loading={busy}
        tone="danger"
        message={(
          <div className="space-y-4">
            <div className="flex items-start gap-2 text-danger bg-danger/10
                            border border-danger/25 rounded-xl px-3 py-2.5">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {org.counts.properties} properties, {org.counts.tenants} tenants and every
                document will be destroyed. This cannot be undone.
              </span>
            </div>
            <Field label={`Type "${org.name}" to confirm`}>
              <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
            </Field>
          </div>
        )}
      />
    </>
  );
}

/** The link, with a copy button — the browser will not let us download it for you. */
function CopyableLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <code className="flex-1 text-xs bg-raised border border-line rounded-xl px-3 py-2.5
                         break-all text-ink">
          {link}
        </code>
        <Button
          variant="secondary"
          icon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          onClick={() => {
            void navigator.clipboard.writeText(link).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="text-sm text-muted leading-relaxed">
        It has also been emailed to them. Read it out only if you are sure who you are
        talking to — anyone holding this link can set that password.
      </p>
    </div>
  );
}


/** "JG Properties" -> "jgproperties", matching what the backend suggests. */
function suggestLocalPart(name: string): string {
  return name.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '').slice(0, 40)
    || 'landlord';
}
